<template>
  <Layout v-if="dataLoaded">
    <b-row class="pb-4 pt-2">
      <b-col>
        <Button location="title" type="white" @click="handleCancelPurchase()"
          >Voltar</Button
        >
      </b-col>
    </b-row>
    <TaxInformation ref="taxInformation" />
    <LinkedDocuments ref="linkedDocuments" v-if="showLinkedDocuments" />
    <Supplier ref="supplier" />
    <Products ref="products" />
    <TaxCalc ref="taxCalc" />
    <Transport ref="transport" />
    <Payment ref="payments" />
    <ModalFiscalProduct />
    <b-row class="margin-button-save" align="center">
      <b-col>
        <Button type="cancel" @click="handleCancelPurchase()">
          <Close class="mb-1" title="Cancelar" /> Cancelar
        </Button>
        <Button type="success" @click="handleUpdatePurchase()">Salvar</Button>
      </b-col>
    </b-row>
  </Layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { Layout, Button } from '@/components';
import Close from 'vue-material-design-icons/Close';
import TaxInformation from '../components/Collapses/TaxInformation/TaxInformation';
import LinkedDocuments from '../components/Collapses/LinkedDocuments/LinkedDocuments';
import Supplier from '../components/Collapses/Supplier';
import Products from '../components/Collapses/Products/Products';
import Transport from '../components/Collapses/Transport';
import TaxCalc from '../components/Collapses/TaxCalc';
import Payment from '../components/Collapses/Payment';
import ModalFiscalProduct from '../components/Modals/ModalFiscalProduct/ModalFiscalProduct';
import { getIdByLastSlash } from '../../../../../reusable/getIdByLastSlash';
import { MODELO } from '../../../../Fiscal/enums/Documento';

export default {
  name: 'purchaseEdit',
  components: {
    Layout,
    Button,
    TaxInformation,
    LinkedDocuments,
    Supplier,
    Products,
    Transport,
    TaxCalc,
    Payment,
    ModalFiscalProduct,
    Close,
  },
  data() {
    return {
      dataLoaded: false,
      collapsesToValidate: ['taxInformation', 'supplier', 'products', 'payments'],
    };
  },
  computed: {
    ...mapState('purchase', ['purchase']),
    ...mapState('fiscalComponents', ['cfopCollection']),
    showLinkedDocuments() {
      const cfop = this.purchase?.taxInformation?.natureza?.cfop?.codigo;
      const cfopWithoutOperation = cfop?.substr(1, 3);
      if (!cfopWithoutOperation) return false;

      const cfopsWatched = ['351', '352', '353'];

      return this.purchase.taxInformation.nfeModel === MODELO.CTE && 
        cfopsWatched.includes(cfopWithoutOperation);
    },
  },
  props: {
    confirm: {
      type: Function,
    },
  },
  async mounted() {
    this.setLoading(true);
    const purchaseId = getIdByLastSlash(this.$route.fullPath);
    if (purchaseId) {
      await this.setDecimalConfiguration();
      await Promise.all([
        this.setDetailedPurchase(purchaseId),
        this.setOrigemCollection(),
        this.setCSTCollection(),
        this.setCSTIPICollection(),
        this.setCSTPISCollection(),
        this.setCSTCOFINSCollection(),
        this.setCENQCollection(),
        this.setCSOSNCollection(),
        this.setUnitOfMeasureCollection(),
        this.setCfopCollection(),
      ]);

      this.purchase.products.productOfPurchaseCollection =
        this.purchase.products.productOfPurchaseCollection.map((e) => {
          const [productCfop] = this.cfopCollection.filter(
            (cfop) => cfop.id === e.fiscal.ids.cfopId,
          );
          e.fiscal.values.cfop = productCfop;

          return e;
        });
    }
    this.dataLoaded = true;
    this.setLoading(false);
  },
  methods: {
    ...mapActions('cenq', ['setCENQCollection']),
    ...mapActions('csosn', ['setCSOSNCollection']),
    ...mapActions('origem', ['setOrigemCollection']),
    ...mapActions('application', ['setLoading']),
    ...mapActions('unitOfMeasure', ['setUnitOfMeasureCollection']),
    ...mapActions('fiscalComponents', ['setCfopCollection']),
    ...mapActions('componentsPurchase', ['toggleModalPriceUpdate']),
    ...mapActions('cst', [
      'setCSTCollection',
      'setCSTIPICollection',
      'setCSTPISCollection',
      'setCSTCOFINSCollection',
    ]),
    ...mapActions('purchase', [
      'setPurchase',
      'updatePurchase',
      'hasInvalidCollapse',
      'updateProductsPrice',
      'setDetailedPurchase',
      'setDecimalConfiguration',
    ]),
    setDefaultFields() {
      this.$refs.payments.setDefaultFields();
    },
    async isValidPurchase() {
      this.$refs.taxInformation.$v.$touch();
      this.$refs.supplier.$v.$touch();
      this.$refs.products.$v.$touch();
      this.$refs.payments.$v.$touch();

      const params = {
        collapsesToValidate: this.collapsesToValidate,
        refs: this.$refs,
        root: this.$root,
      };

      if (await this.hasInvalidCollapse(params)) return false;
      return true;
    },
    async handleUpdatePurchase() {
      this.setDefaultFields();
      if (!(await this.isValidPurchase())) return false;

      await this.setPurchase();

      if (this.entryDateIsLowerThanIssueDate()) {
        return this.$alert.warning(
          'Data de entrada não pode ser menor que data de emissão.',
        );
      }

      if (!this.validatePayment()) {
        return this.confirm({
          title: 'Confirmação',
          message:
            'As formas de pagamento não correspondem com o total da nota, deseja continuar?',
          onOk: () => {
            this.registerPurchase();
          },
        });
      }

      return this.registerPurchase();
    },
    async registerPurchase(productsToUpdatePrice = []) {
      this.setLoading(true);

      try {
        await this.updatePurchase(this.purchase);
        if (productsToUpdatePrice && productsToUpdatePrice.length) {
          await this.updateProductsPrice(productsToUpdatePrice);
        }
      } finally {
        this.setLoading(false);
        const naturezaId = this.purchase.taxInformation.natureza.id;
        localStorage.setItem('lastPurchaseNaturezaId', naturezaId);
      }
      this.$alert.success('Compra salva com sucesso!');
      return this.$router.push({ path: '/inventory/purchase' });
    },
    validatePayment() {
      const NONE_PAYMENT = 'Nenhum';
      const purchasePaymentName =
        this.purchase.payments.paymentModeCollection[0].name;
      const totalNF = this.purchase.taxCalc.vNF;
      const totalPayment = this.purchase.payments.paymentModeCollection.reduce(
        (acc, item) => acc + (item.value ?? 0),
        0,
      );

      if (purchasePaymentName !== NONE_PAYMENT && totalPayment !== totalNF) {
        return false;
      }

      return true;
    },
    entryDateIsLowerThanIssueDate() {
      const entryDate = this.$moment(
        this.purchase.taxInformation.entryDate,
      ).format('YYYY-MM-DD');
      const issueDate = this.$moment(
        this.purchase.taxInformation.issueDate,
      ).format('YYYY-MM-DD');
      return entryDate < issueDate;
    },
    handleCancelPurchase() {
      return this.$router.push({ name: 'Purchase' });
    },
  },
};
</script>
